import { useState, useEffect } from 'react';
import { Pencil, Trash, DotsThree, Plus, ArrowsClockwise, ArrowSquareOut } from '@phosphor-icons/react';
import { adminFunctions } from '../../firebase/adminFunctions';
import { runMigration } from '../../firebase/migrations';
import { featureCategories, addFeatureToCategory, addCategory, removeCategory } from '../../data/filterOptions';
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Badge,
  IconButton,
  Box,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  Divider,
  Tag,
  TagLabel,
  Spacer
} from '@chakra-ui/react';

export default function FeatureOptionsManager() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [movingFeature, setMovingFeature] = useState(null);
  const [isMovingFeatureModalOpen, setIsMovingFeatureModalOpen] = useState(false);
  const [newFeature, setNewFeature] = useState({
    id: '',
    label: '',
    category: '',
  });
  const [newCategory, setNewCategory] = useState({
    id: '',
    label: ''
  });

  const toast = useToast();
  const bgHover = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const featuresData = await adminFunctions.getAllFeatures();
      const processedFeatures = Object.entries(featuresData).map(([id, feature]) => ({
        ...feature,
        id,
      }));
      setFeatures(processedFeatures);
    } catch (error) {
      toast({
        title: 'Error loading features',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = async () => {
    if (!newFeature.id || !newFeature.label || !newFeature.category) {
      toast({
        title: 'Required fields missing',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await adminFunctions.createFeature(newFeature);
      addFeatureToCategory(newFeature.id, newFeature.category);
      await loadFeatures();
      setIsAddModalOpen(false);
      setNewFeature({
        id: '',
        label: '',
        category: ''
      });
      toast({
        title: 'Feature created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error creating feature',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.id || !newCategory.label) {
      toast({
        title: 'Required fields missing',
        description: 'Please provide both category ID and label',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (addCategory(newCategory.id, newCategory.label)) {
      toast({
        title: 'Category added',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      setIsAddCategoryModalOpen(false);
      setNewCategory({ id: '', label: '' });
    } else {
      toast({
        title: 'Category already exists',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleUpdateFeature = async (updates) => {
    if (!updates.category) {
      toast({
        title: 'Category required',
        description: 'Please select a category',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const prevCategory = editingFeature.category;
      await adminFunctions.updateFeature(editingFeature.id, updates);
      if (prevCategory !== updates.category) {
        if (featureCategories[prevCategory]) {
          featureCategories[prevCategory].features = featureCategories[prevCategory].features
            .filter(id => id !== editingFeature.id);
        }
        addFeatureToCategory(editingFeature.id, updates.category);
      }
      await loadFeatures();
      setEditingFeature(null);
      toast({
        title: 'Feature updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating feature',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMoveFeature = async (featureId, newCategory) => {
    const feature = features.find(f => f.id === featureId);
    if (!feature) return;

    setLoading(true);
    try {
      const prevCategory = feature.category;
      await adminFunctions.updateFeature(featureId, {
        ...feature,
        category: newCategory
      });

      if (featureCategories[prevCategory]) {
        featureCategories[prevCategory].features = featureCategories[prevCategory].features
          .filter(id => id !== featureId);
      }
      addFeatureToCategory(featureId, newCategory);

      await loadFeatures();
      setMovingFeature(null);
      setIsMovingFeatureModalOpen(false);
      
      toast({
        title: 'Feature moved',
        description: `Moved to ${featureCategories[newCategory].label}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error moving feature',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (!window.confirm('Are you sure you want to delete this feature? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await adminFunctions.deleteFeature(featureId);
      await loadFeatures();
      toast({
        title: 'Feature deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting feature',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureMigration = async () => {
    if (!window.confirm('Are you sure you want to run the feature consolidation migration? This will update all features to the new format.')) {
      return;
    }

    setMigrating(true);
    try {
      await runMigration('featureConsolidation');
      await loadFeatures();
      toast({
        title: 'Migration successful',
        description: 'All features have been updated to the new format',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Migration failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMigrating(false);
    }
  };

  const filteredFeatures = features.filter(feature =>
    feature.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    feature.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFeatureTypeBadge = (feature) => {
    switch (feature.source) {
      case 'system':
        return <Badge colorScheme="blue">System</Badge>;
      case 'auto':
        return <Badge colorScheme="purple">Auto</Badge>;
      default:
        return <Badge colorScheme="green">Custom</Badge>;
    }
  };

  const getCategoryLabel = (categoryKey) => {
    return featureCategories[categoryKey]?.label || categoryKey;
  };

  const getCategoryFeatures = (categoryKey) => {
    return features.filter(feature => feature.category === categoryKey);
  };

  return (
    <Box>
      <Tabs index={activeTab} onChange={setActiveTab}>
        <TabList mb={4}>
          <Tab>Features List</Tab>
          <Tab>Categories</Tab>
        </TabList>

        <TabPanels>
          {/* Features List Panel */}
          <TabPanel>
            <VStack spacing={4}>
              {features.length === 0 ? (
                <Alert status="warning" mb={4}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Initialize Predefined Features</AlertTitle>
                    <AlertDescription>
                      No features found. Initialize the system with predefined features and categories.
                      <Button
                        ml={4}
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<Plus />}
                        onClick={() => runMigration('initializeFeatures')}
                        isLoading={migrating}
                      >
                        Initialize Features
                      </Button>
                    </AlertDescription>
                  </Box>
                </Alert>
              ) : (
              <VStack spacing={2} width="100%">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Feature System Update</AlertTitle>
                    <AlertDescription>
                      Run the migration to update all features to the new source-based format.
                      <Button
                        ml={4}
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<ArrowsClockwise />}
                        onClick={handleFeatureMigration}
                        isLoading={migrating}
                      >
                        Run Migration
                      </Button>
                    </AlertDescription>
                  </Box>
                </Alert>

                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Categorize Features</AlertTitle>
                    <AlertDescription>
                      Set categories for uncategorized features based on predefined mappings.
                      <Button
                        ml={4}
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<ArrowsClockwise />}
                        onClick={() => runMigration('setCategoryFeatures')}
                        isLoading={migrating}
                      >
                        Set Categories
                      </Button>
                    </AlertDescription>
                  </Box>
                </Alert>
              </VStack>
              )}

              <HStack w="100%" justify="space-between" align="flex-start" spacing={4}>
                <VStack align="stretch" spacing={4} flex={1}>
                  <Input
                    placeholder="Search features..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Select
                    placeholder="Filter by category"
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                  >
                    <option value="">All Categories</option>
                    {Object.entries(featureCategories).map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                </VStack>
                <Button
                  leftIcon={<Plus size={20} />}
                  colorScheme="blue"
                  onClick={() => setIsAddModalOpen(true)}
                >
                  Add Feature
                </Button>
              </HStack>

              <Box overflowX="auto" w="100%">
                {loading ? (
                  <Box textAlign="center" py={8}>
                    <Spinner />
                  </Box>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Feature</Th>
                        <Th>Category</Th>
                        <Th>Type</Th>
                        <Th width="100px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredFeatures
                        .filter(f => !selectedCategory || f.category === selectedCategory)
                        .map((feature) => (
                        <Tr 
                          key={feature.id}
                          _hover={{ bg: bgHover }}
                        >
                          <Td>
                            <HStack>
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="medium">{feature.label}</Text>
                                <Text fontSize="xs" color="gray.500">{feature.id}</Text>
                              </VStack>
                            </HStack>
                          </Td>
                          <Td>
                            <Tag size="sm" colorScheme="purple" variant="subtle">
                              {getCategoryLabel(feature.category)}
                            </Tag>
                          </Td>
                          <Td>
                            {getFeatureTypeBadge(feature)}
                          </Td>
                          <Td>
                            <ButtonGroup size="sm" variant="ghost">
                              <IconButton
                                aria-label="Edit feature"
                                icon={<Pencil size={16} />}
                                onClick={() => setEditingFeature(feature)}
                              />
                              <IconButton
                                aria-label="Move feature"
                                icon={<ArrowSquareOut size={16} />}
                                onClick={() => {
                                  setMovingFeature(feature);
                                  setIsMovingFeatureModalOpen(true);
                                }}
                              />
                              {feature.source !== 'system' && (
                                <IconButton
                                  aria-label="Delete feature"
                                  icon={<Trash size={16} />}
                                  colorScheme="red"
                                  onClick={() => handleDeleteFeature(feature.id)}
                                />
                              )}
                            </ButtonGroup>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </VStack>
          </TabPanel>

          {/* Categories Panel */}
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Alert flex={1}>
                  <AlertIcon />
                  Features are organized into categories to make them easier to find and manage.
                </Alert>
                <Button
                  leftIcon={<Plus />}
                  colorScheme="blue"
                  onClick={() => setIsAddCategoryModalOpen(true)}
                >
                  Add Category
                </Button>
              </HStack>

              <Accordion defaultIndex={[0]} allowMultiple>
                {Object.entries(featureCategories).map(([key, category]) => {
                  const categoryFeatures = getCategoryFeatures(key);
                  
                  return (
                    <AccordionItem key={key}>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold">{category.label}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {categoryFeatures.length} features
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        {categoryFeatures.length > 0 ? (
                          <VStack align="stretch" spacing={2}>
                            {categoryFeatures.map(feature => (
                              <HStack key={feature.id} p={2} bg={bgHover} borderRadius="md">
                                <Tag size="md" variant="subtle">
                                  <TagLabel>{feature.label}</TagLabel>
                                </Tag>
                                <Spacer />
                                <HStack spacing={2}>
                                  <IconButton
                                    icon={<Pencil size={16} />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Edit feature"
                                    onClick={() => setEditingFeature(feature)}
                                  />
                                  <IconButton
                                    icon={<ArrowSquareOut size={16} />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Move feature"
                                    onClick={() => {
                                      setMovingFeature(feature);
                                      setIsMovingFeatureModalOpen(true);
                                    }}
                                  />
                                </HStack>
                              </HStack>
                            ))}
                          </VStack>
                        ) : (
                          <Text color="gray.500">No features in this category</Text>
                        )}
                        <Divider my={4} />
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Category ID: {key}
                          </Text>
                          {categoryFeatures.length === 0 && (
                            <IconButton
                              icon={<Trash size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              aria-label="Delete category"
                              onClick={() => {
                                if (removeCategory(key)) {
                                  toast({
                                    title: 'Category deleted',
                                    status: 'success',
                                    duration: 3000,
                                    isClosable: true
                                  });
                                }
                              }}
                            />
                          )}
                        </HStack>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Add Feature Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Feature</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Feature ID</FormLabel>
                <Input
                  value={newFeature.id}
                  onChange={(e) => setNewFeature({...newFeature, id: e.target.value})}
                  placeholder="e.g., toolMarks"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  value={newFeature.label}
                  onChange={(e) => setNewFeature({...newFeature, label: e.target.value})}
                  placeholder="e.g., Tool Marks"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  placeholder="Select a category"
                  value={newFeature.category}
                  onChange={(e) => setNewFeature({...newFeature, category: e.target.value})}
                >
                  {Object.entries(featureCategories).map(([key, category]) => (
                    <option key={key} value={key}>
                      {category.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddFeature}>
              Add Feature
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Feature Modal */}
      <Modal
        isOpen={!!editingFeature}
        onClose={() => setEditingFeature(null)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Feature</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {editingFeature && (
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Feature ID</FormLabel>
                  <Input
                    value={editingFeature.id}
                    isReadOnly
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel>Label</FormLabel>
                  <Input
                    value={editingFeature.label}
                    onChange={(e) => setEditingFeature({...editingFeature, label: e.target.value})}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Select
                    placeholder="Select a category"
                    value={editingFeature.category || ''}
                    onChange={(e) => setEditingFeature({...editingFeature, category: e.target.value})}
                  >
                    {Object.entries(featureCategories).map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setEditingFeature(null)}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => handleUpdateFeature({
                label: editingFeature.label,
                category: editingFeature.category,
              })}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Move Feature Modal */}
      <Modal
        isOpen={isMovingFeatureModalOpen}
        onClose={() => {
          setIsMovingFeatureModalOpen(false);
          setMovingFeature(null);
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Move Feature</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {movingFeature && (
              <VStack spacing={4}>
                <Text>
                  Moving <strong>{movingFeature.label}</strong> from{' '}
                  <strong>{getCategoryLabel(movingFeature.category)}</strong>
                </Text>
                
                <FormControl isRequired>
                  <FormLabel>New Category</FormLabel>
                  <Select
                    placeholder="Select new category"
                    onChange={(e) => handleMoveFeature(movingFeature.id, e.target.value)}
                  >
                    {Object.entries(featureCategories)
                      .filter(([key]) => key !== movingFeature.category)
                      .map(([key, category]) => (
                        <option key={key} value={key}>
                          {category.label}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => {
              setIsMovingFeatureModalOpen(false);
              setMovingFeature(null);
            }}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Category Modal */}
      <Modal
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Category</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Category ID</FormLabel>
                <Input
                  value={newCategory.id}
                  onChange={(e) => setNewCategory({...newCategory, id: e.target.value})}
                  placeholder="e.g., structural"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  value={newCategory.label}
                  onChange={(e) => setNewCategory({...newCategory, label: e.target.value})}
                  placeholder="e.g., Structural Features"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsAddCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddCategory}>
              Add Category
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
